import React, { useMemo } from "react";
import { Box, Typography, IconButton, useMediaQuery, useTheme } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(customParseFormat);

function parseDayOfActivity(val) {
  if (!val) return null;
  const parsed = dayjs(val, "DD/MM/YYYY", true);
  if (parsed.isValid()) return parsed;
  return dayjs(val);
}

function groupByDate(data) {
  return data.reduce((acc, item) => {
    if (!item.day_of_activity) return acc;
    const parsed = parseDayOfActivity(item.day_of_activity);
    if (!parsed || !parsed.isValid()) return acc;
    const key = parsed.format("YYYY-MM-DD");
    if (!acc[key]) acc[key] = [];
    acc[key].push(item);
    return acc;
  }, {});
}

const WEEKDAYS_FULL = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const WEEKDAYS_SHORT = ["M", "T", "W", "T", "F", "S", "S"];

export default function CalendarMonthGrid({
  data,
  currentMonth,
  onMonthChange,
  onDateClick,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const grouped = useMemo(() => groupByDate(data), [data]);
  const today = dayjs().format("YYYY-MM-DD");
  const weekdays = isMobile ? WEEKDAYS_SHORT : WEEKDAYS_FULL;

  const monthStart = currentMonth.startOf("month");
  const daysInMonth = currentMonth.daysInMonth();

  const rawOffset = monthStart.day();
  const startOffset = rawOffset === 0 ? 6 : rawOffset - 1;

  const totalCells = Math.ceil((startOffset + daysInMonth) / 7) * 7;
  const cells = Array.from({ length: totalCells }, (_, i) => {
    const dayNum = i - startOffset + 1;
    if (dayNum < 1 || dayNum > daysInMonth) return null;
    return dayNum;
  });

  return (
    <Box sx={{ pb: 3, overflow: "hidden" }}>
      {/* Month navigation */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
          mb: 2,
        }}
      >
        <IconButton
          size="small"
          onClick={() => onMonthChange(currentMonth.subtract(1, "month"))}
          sx={{ border: "1px solid #E0E0E0", borderRadius: "50%" }}
        >
          <ChevronLeftIcon />
        </IconButton>
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: 16,
            minWidth: 140,
            textAlign: "center",
          }}
        >
          {currentMonth.format("MMMM YYYY")}
        </Typography>
        <IconButton
          size="small"
          onClick={() => onMonthChange(currentMonth.add(1, "month"))}
          sx={{ border: "1px solid #E0E0E0", borderRadius: "50%" }}
        >
          <ChevronRightIcon />
        </IconButton>
      </Box>

      {/* Weekday header */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
          mb: 0.5,
        }}
      >
        {weekdays.map((d, i) => (
          <Box key={i} sx={{ textAlign: "center", py: 0.8 }}>
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 700,
                color: "#9E9E9E",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              {d}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Day cells */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
          border: "1px solid #E8E8E8",
          borderRadius: "10px",
          overflow: "hidden",
        }}
      >
        {cells.map((dayNum, idx) => {
          if (dayNum === null) {
            return (
              <Box
                key={`blank-${idx}`}
                sx={{
                  minHeight: isMobile ? 48 : 90,
                  backgroundColor: "#FAFAFA",
                  borderRight:
                    idx % 7 !== 6 ? "1px solid #F0F0F0" : "none",
                  borderBottom: "1px solid #F0F0F0",
                }}
              />
            );
          }

          const dateKey = currentMonth.date(dayNum).format("YYYY-MM-DD");
          const isToday = dateKey === today;
          const items = grouped[dateKey] || [];

          return (
            <Box
              key={dateKey}
              onClick={() => items.length > 0 && onDateClick(dateKey, items)}
              sx={{
                minHeight: isMobile ? 48 : 90,
                p: isMobile ? 0.4 : 0.75,
                borderRight:
                  idx % 7 !== 6 ? "1px solid #F0F0F0" : "none",
                borderBottom: "1px solid #F0F0F0",
                backgroundColor: isToday ? "#FFFDE7" : "#fff",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 0.3,
                overflow: "hidden",
                minWidth: 0,
                cursor: items.length > 0 ? "pointer" : "default",
                "&:hover": items.length > 0 ? {
                  backgroundColor: isToday ? "#FFF9C4" : "#F8FBFF",
                } : {},
              }}
            >
              {/* Day number */}
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: "50%",
                  backgroundColor: isToday ? "#F9A825" : "transparent",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography
                  sx={{
                    fontSize: 12,
                    fontWeight: isToday ? 700 : 500,
                    color: isToday ? "#fff" : "#333",
                  }}
                >
                  {dayNum}
                </Typography>
              </Box>

              {/* Mobile: show count dot. Desktop: show booking chips */}
              {isMobile ? (
                items.length > 0 && (
                  <Box
                    sx={{
                      backgroundColor: "#1976D2",
                      color: "#fff",
                      borderRadius: "10px",
                      px: 0.6,
                      py: 0.1,
                      fontSize: 9,
                      fontWeight: 700,
                      lineHeight: 1.4,
                    }}
                  >
                    {items.length}
                  </Box>
                )
              ) : (
                <>
                  {items.slice(0, 3).map((item) => (
                    <Box
                      key={item._id}
                      sx={{
                        width: "100%",
                        backgroundColor: "#EBF4FF",
                        color: "#1565C0",
                        borderRadius: "4px",
                        px: 0.5,
                        py: 0.1,
                        fontSize: 10,
                        fontWeight: 600,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        lineHeight: 1.6,
                      }}
                    >
                      {item.booking_number}
                    </Box>
                  ))}
                  {items.length > 3 && (
                    <Typography
                      sx={{
                        fontSize: 10,
                        color: "#1976D2",
                        fontWeight: 600,
                      }}
                    >
                      +{items.length - 3} more
                    </Typography>
                  )}
                </>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
